import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useLanguage } from '../context/LanguageContext';
import BrokerAvatarUpload from '../components/BrokerAvatarUpload';
import './ProfilePage.css';
import '../styles/userPages.css';

function IconCheck() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;
}

export default function ProfilePage() {
  const { user, profile, logout, hasProAccess, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [form, setForm] = useState({
    firstName: user?.firstName || '',
    lastName:  user?.lastName  || '',
    email:     user?.email     || '',
    phone:     user?.phone     || '',
    bio:       user?.bio       || '',
  });
  const [saved, setSaved]               = useState(false);
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState('');
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError]     = useState('');

  if (!user) { navigate('/login'); return null; }

  const openBillingPortal = async () => {
    setPortalError('');
    setPortalLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        setPortalError(t('profile.portalNotSignedIn'));
        return;
      }
      const res = await fetch('/api/create-portal-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (res.ok && data.url) {
        window.location.href = data.url;
        return;
      }
      setPortalError(data.error === 'no_subscription' ? t('profile.portalNoSubscription') : t('profile.portalFailed'));
    } catch (err) {
      console.error('Portal session error:', err);
      setPortalError(t('profile.portalFailed'));
    } finally {
      setPortalLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    const { error: err } = await supabase
      .from('profiles')
      .update({
        first_name: form.firstName,
        last_name:  form.lastName,
        phone:      form.phone,
      })
      .eq('id', user.id);

    setSaving(false);
    if (err) {
      setError(t('profile.failedSave'));
    } else {
      await refreshProfile();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
  };

  return (
    <div className="profile-page">
      <div className="profile-page__inner">

        <div className="profile-page__sidebar">
          <div className="profile-page__avatar">
            {profile?.is_broker && profile?.partner_status === 'approved' && profile?.avatar_url
              ? <img src={profile.avatar_url} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              : user.avatar}
          </div>
          <div className="profile-page__name">{user.firstName} {user.lastName}</div>
          <div className="profile-page__email">{user.email}</div>
          <nav className="profile-page__nav">
            <button className="profile-page__nav-item profile-page__nav-item--active" onClick={() => navigate('/profile')}>{t('nav.myProfile')}</button>
            <button className="profile-page__nav-item" onClick={() => navigate('/my-listings')}>{t('nav.myListings')}</button>
            <button className="profile-page__nav-item" onClick={() => navigate('/inbox')}>{t('nav.inbox')}</button>
            <button className="profile-page__nav-item" onClick={() => navigate('/settings')}>{t('nav.settings')}</button>
            <button className="profile-page__nav-item profile-page__nav-item--logout" onClick={() => { logout(); navigate('/'); }}>{t('nav.logout')}</button>
          </nav>
        </div>

        <div className="profile-page__content">
          <div className="profile-page__section-header">
            <h1 className="profile-page__title">{t('profile.title')}</h1>
            <p className="profile-page__sub">{t('profile.sub')}</p>
          </div>

          {profile?.is_broker && (
            <div className="profile-page__card">
              <h2 className="profile-page__card-title">Profile photo</h2>
              <BrokerAvatarUpload
                currentAvatarUrl={profile?.avatar_url}
                userId={user?.id}
                brokerId={null}
                userEmail={user?.email}
                onUploadComplete={() => refreshProfile()}
              />
            </div>
          )}

          <div className="profile-page__card">
            <h2 className="profile-page__card-title">{t('profile.personalDetails')}</h2>
            <div className="profile-page__row">
              <div className="profile-page__field">
                <label>{t('profile.firstName')}</label>
                <input value={form.firstName} onChange={e => setForm(f => ({...f, firstName: e.target.value}))} />
              </div>
              <div className="profile-page__field">
                <label>{t('profile.lastName')}</label>
                <input value={form.lastName} onChange={e => setForm(f => ({...f, lastName: e.target.value}))} />
              </div>
            </div>
            <div className="profile-page__field">
              <label>{t('profile.emailAddress')}</label>
              <input type="email" value={form.email} disabled style={{ opacity: 0.6, cursor: 'not-allowed' }} />
              <span style={{ fontSize: 11, color: '#9ca3af', marginTop: 4, display: 'block' }}>{t('profile.emailCannotChange')}</span>
            </div>
            <div className="profile-page__field">
              <label>{t('profile.phoneNumber')}</label>
              <input type="tel" placeholder="+357 99 000 000" value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} />
            </div>
            <div className="profile-page__actions">
              {error && <span style={{ fontSize: 12, color: '#ef4444' }}>{error}</span>}
              {saved && <span className="profile-page__saved"><IconCheck /> {t('profile.changesSaved')}</span>}
              <button className="profile-page__btn" onClick={handleSave} disabled={saving}>
                {saving ? t('profile.saving') : t('profile.saveChanges')}
              </button>
            </div>
          </div>

          <div className="profile-page__card">
            <h2 className="profile-page__card-title">{t('profile.proMembership')}</h2>
            <div className="profile-page__pro-banner">
              <div className="profile-page__pro-info">
                {hasProAccess ? (
                  <>
                    <p>{t('profile.proActiveMsg')}</p>
                  </>
                ) : (
                  <>
                    <div className="profile-page__pro-badge">{t('profile.freePlan')}</div>
                    <p>{t('profile.freeMsg')}</p>
                  </>
                )}
              </div>
              {hasProAccess
                ? Boolean(profile?.stripe_customer_id) && (
                    <button
                      className="profile-page__pro-btn profile-page__pro-btn--manage"
                      onClick={openBillingPortal}
                      disabled={portalLoading}
                    >
                      {portalLoading ? t('profile.portalLoading') : t('profile.manageSubscription')}
                    </button>
                  )
                : (
                    <button className="profile-page__pro-btn" onClick={() => navigate('/checkout')}>
                      {t('profile.upgradePro')}
                    </button>
                  )}
            </div>

            {/*
              Fallback shown to Pro users whose stripe_customer_id has not been
              backfilled yet (pre-portal-rollout customers). After the one-time
              backfill in supabase/migrations/backfill_stripe_customer_id_TEMPLATE.sql
              runs, this card should appear for near-zero users.
            */}
            {hasProAccess && !profile?.stripe_customer_id && (
              <div className="profile-page__portal-fallback">
                <p>
                  {t('profile.portalMissingBefore')}
                  <a href="mailto:cosmoestatescyprus@gmail.com?subject=Subscription%20Management%20Request">
                    cosmoestatescyprus@gmail.com
                  </a>
                  {t('profile.portalMissingAfter')}
                </p>
              </div>
            )}

            {portalError && (
              <p className="profile-page__portal-error">{portalError}</p>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
